// PATH: src/components/catalog/parts.tsx
/** Briques des panneaux de détail : en-tête, section, ligne de liste, fiche. */
import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MoreIcon from "@mui/icons-material/MoreVert";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import { tokens } from "../../theme/theme";
import { EmptyState } from "../ui";

// ---------------------------------------------------------------------------
// En-tête du nœud
// ---------------------------------------------------------------------------

export interface Crumb {
  label: string;
  onClick?: () => void;
}

interface HeaderProps {
  /** « Programme », « Étape », « Niveau » */
  kind: string;
  crumbs: Crumb[];
  title: React.ReactNode;
  code: string;
  /** Pastilles à côté du titre (statut). */
  badges?: React.ReactNode;
  /** Visuel à gauche du titre (astre du programme). */
  leading?: React.ReactNode;
  actions?: React.ReactNode;
  /** Entrées du menu ⋮ (la suppression, en dernier). */
  onDelete?: () => void;
  deleteLabel?: string;
}

export const DetailHeader: React.FC<HeaderProps> = ({
  kind,
  crumbs,
  title,
  code,
  badges,
  leading,
  actions,
  onDelete,
  deleteLabel = "Supprimer",
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  return (
    <Box sx={{ mb: 2.5 }}>
      {crumbs.length > 0 && (
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: 16 }} />}
          sx={{ mb: 0.75, display: { xs: "none", md: "block" }, "& .MuiBreadcrumbs-li": { fontSize: "0.8125rem" } }}
        >
          {crumbs.map((c, i) =>
            c.onClick ? (
              <Link key={i} component="button" type="button" underline="hover" color="text.secondary" onClick={c.onClick} dir="auto">
                {c.label}
              </Link>
            ) : (
              <Typography key={i} variant="body2" color="text.secondary" dir="auto">
                {c.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {leading}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {kind}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h2" component="h2" dir="auto" sx={{ wordBreak: "break-word" }}>
                {title}
              </Typography>
              <Typography className="mono" color="text.secondary" sx={{ fontSize: "0.875rem !important" }}>
                {code}
              </Typography>
              {badges}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {actions}
          {onDelete && (
            <>
              <Tooltip title="Plus d'actions">
                <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Plus d'actions">
                  <MoreIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setAnchor(null);
                    onDelete();
                  }}
                  sx={{ color: "error.dark" }}
                >
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{deleteLabel}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Fiche clé / valeur
// ---------------------------------------------------------------------------

export const Facts: React.FC<{ items: Array<{ label: string; value: React.ReactNode }> }> = ({ items }) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: "grid",
      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", lg: "repeat(auto-fit, minmax(80px, 1fr))" },
      gap: 2,
    }}
  >
    {items.map((it) => (
      <Box key={it.label} sx={{ minWidth: 0 }}>
        <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
          {it.label}
        </Typography>
        <Typography component="dd" variant="body1" sx={{ m: 0, fontWeight: 500 }} noWrap>
          {it.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  /** Une phrase : ce que la section contient. */
  description?: React.ReactNode;
  count?: number;
  addLabel?: string;
  onAdd?: () => void;
  /** Filtre texte (longues listes). */
  filter?: { value: string; onChange: (v: string) => void; placeholder: string };
  /** Contenu à droite du titre (bouton secondaire). */
  extra?: React.ReactNode;
  children: React.ReactNode;
  /** Pas de rembourrage (listes). */
  flush?: boolean;
}

export const Section: React.FC<SectionProps> = ({ title, description, count, addLabel, onAdd, filter, extra, children, flush }) => (
  <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${tokens.border}` }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="subtitle1">{title}</Typography>
          {count !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {count}
            </Typography>
          )}
        </Stack>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
        {filter && (
          <TextField
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            placeholder={filter.placeholder}
            sx={{ width: { xs: "100%", sm: 200 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        {extra}
        {onAdd && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAdd} sx={{ flexShrink: 0 }}>
            {addLabel ?? "Ajouter"}
          </Button>
        )}
      </Stack>
    </Stack>
    <Box sx={flush ? undefined : { p: 2.5 }}>{children}</Box>
  </Paper>
);

export const SectionEmpty: React.FC<{ title: string; description?: string; action?: React.ReactNode }> = (p) => (
  <EmptyState dense {...p} />
);

// ---------------------------------------------------------------------------
// Ligne de liste
// ---------------------------------------------------------------------------

interface RowProps {
  /** Petit repère à gauche (rang, pastille). */
  lead?: React.ReactNode;
  primary: React.ReactNode;
  code?: string;
  /** Ligne secondaire (compteurs, filières). */
  secondary?: React.ReactNode;
  /** Contenu à droite, avant les actions (pastilles). */
  meta?: React.ReactNode;
  /** Ouvre l'élément (étape → niveaux…) : la ligne devient un lien. */
  onOpen?: () => void;
  openLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  dimmed?: boolean;
}

export const ListRow: React.FC<RowProps> = ({
  lead,
  primary,
  code,
  secondary,
  meta,
  onOpen,
  openLabel,
  onEdit,
  onDelete,
  dimmed,
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{
      px: 2.5,
      py: 1,
      minHeight: 48,
      borderTop: `1px solid ${tokens.border}`,
      "&:first-of-type": { borderTop: 0 },
      "&:hover": { bgcolor: "action.hover" },
      "&:hover .row-actions, &:focus-within .row-actions": { opacity: 1 },
      opacity: dimmed ? 0.65 : 1,
    }}
  >
    {lead !== undefined && (
      <Box sx={{ width: 28, flexShrink: 0, display: "flex", justifyContent: "center", color: "text.secondary" }}>{lead}</Box>
    )}
    <Box
      sx={{ flex: 1, minWidth: 0, cursor: onOpen ? "pointer" : "default" }}
      onClick={onOpen}
      role={onOpen ? "link" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={openLabel}
      onKeyDown={(e) => {
        if (onOpen && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 550,
            flexShrink: 0,
            maxWidth: code ? "75%" : "100%",
            ...(onOpen ? { "&:hover": { color: "primary.main" } } : {}),
          }}
          noWrap
          dir="auto"
        >
          {primary}
        </Typography>
        {code && (
          <Typography variant="caption" className="mono" color="text.secondary" noWrap sx={{ minWidth: 0 }} title={code}>
            {code}
          </Typography>
        )}
      </Stack>
      {secondary && (
        <Box sx={{ mt: 0.25, color: "text.secondary", fontSize: "0.8125rem", minWidth: 0 }}>{secondary}</Box>
      )}
    </Box>
    {meta && <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>{meta}</Box>}
    <Stack
      direction="row"
      spacing={0.25}
      className="row-actions"
      sx={{ flexShrink: 0, opacity: { xs: 1, md: 0.35 }, transition: "opacity 120ms" }}
    >
      {onEdit && (
        <Tooltip title="Modifier">
          <IconButton size="small" onClick={onEdit} aria-label="Modifier">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={onDelete} aria-label="Supprimer" sx={{ "&:hover": { color: "error.main" } }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onOpen && (
        <Tooltip title={openLabel ?? "Ouvrir"}>
          <IconButton size="small" onClick={onOpen} aria-label={openLabel ?? "Ouvrir"}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Stack>
);

/** Rang affiché en tête de ligne. */
export const Rank: React.FC<{ n: number }> = ({ n }) => (
  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
    {n}
  </Typography>
);
